import Color from "color";
import { styled } from "styled-components";

import { isDark } from "simplydo/react/theme";

// Component that sets/overrides a bunch of default stylings, particularly to set the font to Inter and to set gentler colors for semantic ui
const AppStyling = styled.div`
  flex: 1;
  max-width: 100%;

  &,
  .ui,
  .ui :not(.icon),
  body,
  h1,
  h2,
  h3,
  h4,
  h5,
  span,
  input,
  p {
    font-family: ${({ theme }) => theme?.fontFamily?.body || "Inter"};
    -webkit-font-smoothing: antialiased;
  }
  h1 {
    font-family: ${({ theme }) => theme?.fontFamily?.h1 || "Inter"};
  }
  h2 {
    font-family: ${({ theme }) => theme?.fontFamily?.h2 || "Inter"};
  }
  h3 {
    font-family: ${({ theme }) => theme?.fontFamily?.h3 || "Inter"};
  }
  h4 {
    font-family: ${({ theme }) => theme?.fontFamily?.h4 || "Inter"};
  }
  h5 {
    font-family: ${({ theme }) => theme?.fontFamily?.h5 || "Inter"};
  }
  .ui.container {
    display: flex;
    flex: 1;
    flex-direction: column;
  }

  .ui.loader {
    z-index: auto;
  }

  .idea img {
    object-fit: contain;
    max-width: 100%;
  }

  #semantic-modal-mount-node.dimmed.dimmable {
    overflow: visible;
  }

  ${({ theme }) => {
    if (!theme) {
      return "";
    }

    let override = `
      a,a:hover{color:${theme.primaryColour};}
      a.with-border{border-bottom:1px dotted ${theme.primaryColour};}
      .primary-text{color: ${theme.primaryColour};}

      .ui.button.basic:not(.inverted):hover,
      .ui.button.basic:not(.inverted):focus {
        background-color: transparent !important;
      }
      .ui.button.basic:not(.inverted):active {
        background-color: rgba(0, 0, 0, .05) !important;
      }

      .ui.button.inverted:not(.basic):focus {
        color: #fff !important;
        background-color: transparent !important;
      }

      .ui.button.inverted:not(.basic):hover,
      .ui.button.inverted:not(.basic):active {
        color: rgba(0, 0, 0, .8) !important;
        background-color: rgba(255, 255, 255, .6) !important;
      }

      .ui.button.inverted:not(.basic):active {
        background-color: rgba(255, 255, 255, .8) !important;
      }

      .ui.button:focus-visible {
        outline: -webkit-focus-ring-color auto 1px !important;
      }

      .ui.button {
        transition: opacity 0.2s ease !important;
        will-change: opacity !important;
      }

      .ui.button.loading {
        pointer-events: none !important;
      }

      .ui.modals {
        display: flex !important;
      }

      .ui.card>.button, .ui.card>.buttons, .ui.cards>.card>.button, .ui.cards>.card>.buttons {
        width: 100%;
        margin: 0;
      }

      .ui.labeled.button .ui.label {
        white-space: nowrap;
      }

      .ui.labeled.button .ui.label.basic {
        font-weight: normal;
      }
    `;

    const colorMap: Record<string, string> = { ...(theme.colorMap || {}) };
    colorMap["primary"] = theme.primaryColour;
    colorMap["secondary"] = theme.secondaryColour;

    Object.entries(colorMap).forEach(([color, overrideColor]) => {
      const colorInstance = Color(overrideColor);

      let textOnWhite = Color(overrideColor).darken(0.5);
      let textOnSolid = Color(overrideColor).darken(0.5);
      if (
        color === "primary" ||
        color === "secondary" ||
        color === "msteams" ||
        color === "violet"
      ) {
        textOnWhite = Color(overrideColor);
        textOnSolid = Color("#000000");

        const colorIsDark = isDark(colorInstance);
        if (colorIsDark) {
          textOnSolid = Color("#ffffff");
        }
      }

      override += `
          .ui.${color}.button, .ui.${color}.label {
            color: ${textOnSolid} !important;
          }

          /* Basic buttons/labels */
          .ui.${color}.label:not(.basic),
          .ui.${color}.button:not(.inverted, .basic) {
            background-color: ${overrideColor} !important;
          }
          .ui.${color}.button:not(.inverted, .basic):hover,
          .ui.${color}.button:not(.inverted, .basic):focus,
          .ui.dropdown:focus .ui.${color}.button:not(.inverted, .basic) {
            background-color: ${colorInstance.darken(0.1)} !important;
          }
          .ui.${color}.button:not(.inverted, .basic):active {
            background-color: ${colorInstance.darken(0.2)} !important;
          }

          /* Basic buttons/labels */
          .ui.${color}.button.basic:not(.inverted),
          .ui.${color}.button.basic:not(.inverted):hover {
            color: ${textOnWhite} !important;
            box-shadow: 0 0 0 1px ${colorInstance} inset !important;
          }
          .ui.${color}.button.basic:not(.inverted):focus,
          .ui.dropdown:focus .ui.${color}.button.basic:not(.inverted),
          .ui.${color}.button.basic:not(.inverted):hover  {
            box-shadow: 0 0 0 1px ${colorInstance.darken(0.3)} inset !important;
          }

          .ui.${color}.button.basic:not(.inverted):active  {
            color: ${textOnSolid} !important;
            background-color: ${colorInstance.lighten(0.2)} !important;
            box-shadow: 0 0 0 1px ${colorInstance.darken(0.2)} inset, 0 1px 4px 0 rgba(34, 36, 38, .15) inset !important;
          }

          /* Inverted buttons/labels */
          .ui.${color}.button.inverted {
            color: ${textOnWhite} !important;
            background-color: transparent !important;
            box-shadow: 0 0 0 2px ${colorInstance} inset !important;
          }
          .ui.${color}.button.inverted:focus,
          .ui.dropdown:focus .ui.${color}.button.inverted,
          .ui.${color}.button.inverted:hover  {
            color: ${textOnSolid} !important;
            box-shadow: 0 0 0 2px ${colorInstance.darken(0.3)} inset !important;
          }

          .ui.${color}.button.inverted:not(.basic):focus,
          .ui.dropdown:focus .ui.${color}.button.inverted:not(.basic) {
            color: ${textOnWhite} !important;
          }

          .ui.${color}.button.inverted:not(.basic):hover {
            color: ${textOnSolid} !important;
            background-color: ${colorInstance} !important;
          }

          .ui.${color}.button.inverted:not(.basic):active {
            background-color: ${colorInstance.darken(0.1)} !important;
          }

          /* Inverted AND basic buttons/labels */
          .ui.${color}.button.inverted.basic {
            color: ${textOnWhite} !important;
            box-shadow: 0 0 0 2px ${colorInstance} inset !important;
          }

          .ui.${color}.button.inverted.basic:hover,
          .ui.${color}.button.inverted.basic:focus,
          .ui.dropdown:focus .ui.${color}.button.inverted.basic {
            color: ${textOnWhite} !important;
            box-shadow: 0 0 0 2px ${colorInstance.darken(0.3)} inset !important;
          }

          .ui.${color}.button.inverted.basic:active {
            color: ${textOnSolid} !important;
            background-color: ${colorInstance.lighten(0.2)} !important;
          }

          /* pastel icons */
          i.icon.${color}.pastel {
            color: ${overrideColor} !important;
          }
          i.${color}.icon.inverted.pastel {
            color: ${colorInstance.darken(0.5)} !important;
            background-color: ${overrideColor} !important;
          }
          `;
    });

    override += `
      /* Base disabled state — unchecked */
      .ui.toggle.checkbox.disabled input[disabled] ~ .box:before,
      .ui.toggle.checkbox.disabled input[disabled] ~ label:before {
        background-color: #dedede !important;
        border-color: #ddddde !important;
      }

      /* Checked + Disabled: keep blue, but dim it */
      .ui.toggle.checkbox.disabled input[disabled]:checked ~ .box:before,
      .ui.toggle.checkbox.disabled input[disabled]:checked ~ label:before {
        background-color: rgba(33, 133, 208, 0.5) !important; /* original blue at 50% opacity */
        border-color: rgba(33, 133, 208, 0.5) !important;
      }
    `;

    return override;
  }}
`;

export default AppStyling;
